import $api from "../api/axios";

export type TestRunStatus = "pending" | "running" | "succeeded" | "failed";
export type TestVerdict = "pending" | "pass" | "fail" | "inconclusive";
export type TestCaseOrigin = "case_brief_capture" | "deficiency";

export type CaseBriefPickerItem = {
  id: number;
  name: string | null;
  organizationName: string | null;
  turnCount: number;
  updatedAt: string;
};

export type TestCaseListItem = {
  id: number;
  name: string;
  featureKey: string;
  origin: TestCaseOrigin;
  sourceCaseBriefId: number | null;
  sourceDeficiencyId: number | null;
  versionCount: number;
  latestVersionId: number | null;
  latestVersion: number | null;
  turnCount: number;
  createdAt: string;
};

export type TestFixture = {
  turns: { content: string }[];
  startingBrief: string;
  language: string;
  dimensions: Record<string, string>;
  vectorStoreId: string | null;
  uploadedFiles: unknown[];
  entryPoint: string;
};

export type TestCaseVersionDetail = {
  id: number;
  version: number;
  contentHash: string;
  checks: string | null;
  fixture: TestFixture;
  capturedAt: string;
};

export type TestCaseDetail = {
  id: number;
  name: string;
  featureKey: string;
  origin: TestCaseOrigin;
  sourceCaseBriefId: number | null;
  sourceDeficiencyId: number | null;
  versions: TestCaseVersionDetail[];
};

export type TranscriptTurn = {
  index: number;
  userText: string;
  intent: string;
  hasNewInformation: boolean;
  shouldBuild: boolean;
  assistantText: string;
  briefChanged: boolean;
  briefAfter: string;
  tools: string[];
};

export type TestRun = {
  id: number;
  revisionId: number;
  status: TestRunStatus;
  phase: string | null;
  turnsCompleted: number;
  transcript: TranscriptTurn[] | null;
  finalBrief: string | null;
  completeness: { overallScore?: number; readyToAdvance?: boolean } | null;
  projectMeta: { name?: string | null; description?: string | null } | null;
  inputTokens: number | null;
  outputTokens: number | null;
  latencyMs: number | null;
  error: string | null;
};

export type ComparisonDetail = {
  id: number;
  candidateRevisionId: number;
  verdict: TestVerdict;
  verdictNote: string | null;
  baselineRun: TestRun;
  candidateRun: TestRun;
  testCaseVersion: { id: number; version: number; checks: string | null; testCase: { id: number; name: string } };
};

export type RevisionComparisonSummary = {
  id: number;
  verdict: TestVerdict;
  verdictNote: string | null;
  createdAt: string;
  baselineRunStatus: TestRunStatus;
  candidateRunStatus: TestRunStatus;
  testCaseName: string;
  testCaseVersion: number;
};

export type ResultsSummaryPointKind = "meaningful" | "cosmetic";

export type ResultsSummary = {
  headline: string;
  points: { text: string; kind: ResultsSummaryPointKind }[];
};

export function formatInputCount(count: number): string {
  return `${count} user input${count === 1 ? "" : "s"}`;
}

export async function listCaseBriefsForCapture(search?: string): Promise<CaseBriefPickerItem[]> {
  const response = await $api.get("/admin/prompt-tests/case-briefs", { params: search ? { search } : undefined });
  return response.data;
}

export async function captureTestCase(body: {
  caseBriefId: number;
  name?: string;
  deficiencyId?: number;
}): Promise<{ testCaseId: number; testCaseVersionId: number; version: number; turnCount: number; reused: boolean }> {
  const response = await $api.post("/admin/prompt-tests/cases/capture", body);
  return response.data;
}

export async function listTestCases(): Promise<TestCaseListItem[]> {
  const response = await $api.get("/admin/prompt-tests/cases");
  return response.data;
}

export async function getTestCase(id: number): Promise<TestCaseDetail> {
  const response = await $api.get(`/admin/prompt-tests/cases/${id}`);
  return response.data;
}

export async function createComparison(body: {
  testCaseVersionId: number;
  candidateRevisionId: number;
}): Promise<{ comparisonId: number; baselineRunId: number; candidateRunId: number }> {
  const response = await $api.post("/admin/prompt-tests/comparisons", body);
  return response.data;
}

// Mirrors MAX_COMPARISONS_PER_BATCH on the backend, which rejects a longer list.
export const MAX_COMPARISONS_PER_BATCH = 10;

export async function createComparisonBatch(body: {
  testCaseVersionIds: number[];
  candidateRevisionId: number;
}): Promise<{ comparisonIds: number[] }> {
  const response = await $api.post("/admin/prompt-tests/comparisons/batch", body);
  return response.data;
}

export type RevisionDiffEntry = {
  promptKey: string;
  label: string;
  changed: boolean;
  leftContent: string;
  rightContent: string;
};

export async function getRevisionDiff(
  revisionId: number,
  againstRevisionId: number
): Promise<{ leftRevisionId: number; rightRevisionId: number; entries: RevisionDiffEntry[] }> {
  const response = await $api.get(`/admin/prompts/revisions/${revisionId}/diff`, {
    params: { against: againstRevisionId }
  });
  return response.data;
}

export async function getComparison(id: number): Promise<ComparisonDetail> {
  const response = await $api.get(`/admin/prompt-tests/comparisons/${id}`);
  return response.data;
}

export async function getComparisonSummary(id: number): Promise<ResultsSummary> {
  const response = await $api.get(`/admin/prompt-tests/comparisons/${id}/summary`);
  return response.data;
}

export async function setComparisonVerdict(id: number, verdict: TestVerdict, note: string | null): Promise<void> {
  await $api.patch(`/admin/prompt-tests/comparisons/${id}/verdict`, { verdict, note });
}

export async function listRevisionComparisons(revisionId: number): Promise<RevisionComparisonSummary[]> {
  const response = await $api.get(`/admin/prompt-tests/revisions/${revisionId}/comparisons`);
  return response.data;
}
